import React from "react";
import { Link } from "react-router-dom";

export default function Terms() {
    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="terms-page">
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Legal</p>
            <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight mb-3">Terms of Service</h1>
            <p className="text-sm text-muted-foreground mb-10">Last updated: February 14, 2026</p>

            <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground leading-relaxed">
                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">1. Who we are</h2>
                    <p>
                        Real-Check.org (&quot;Real-Check&quot;, &quot;we&quot;, &quot;us&quot;) is an AI-powered fact-checking and scam-detection
                        service operated by Chris, a disabled veteran and founder of the <strong className="text-emerald-400">Hands of Hope</strong> mission
                        (2025). By using Real-Check you agree to these Terms.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">2. What Real-Check does — and doesn&#39;t do</h2>
                    <p>
                        Real-Check uses large language models (currently OpenAI GPT-5.2 with vision and OpenAI Whisper for voice) to analyze
                        claims, images, and audio you submit, and returns a traffic-light verdict, plain-language explanation, and red-flag
                        indicators for scams.
                    </p>
                    <p>
                        Real-Check is <strong className="text-accent">not</strong> a licensed attorney, doctor, financial advisor, or law-enforcement
                        agency. Verdicts are AI-generated assessments and can be wrong. Do not rely on Real-Check as your only source when
                        making legal, medical, financial, or safety decisions. If you believe you&#39;ve been the victim of a crime, contact your
                        local authorities.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">3. Accounts &amp; usage limits</h2>
                    <p>
                        You may use the free tier without an account (subject to anonymous rate limits) or create an account to save history and
                        access a higher free daily limit. Real-Check Pro is a paid subscription that removes daily limits, hides ads, and unlocks
                        saved history. You are responsible for keeping your account credentials secure.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">4. Donations &amp; merch pre-orders</h2>
                    <p>
                        Donations processed through Real-Check are directed to the Hands of Hope charity mission. Merch pre-orders are charged
                        immediately via Stripe; 50% of each pre-order funds Hands of Hope and 50% covers production, shipping, and keeps
                        Real-Check ad-free and independent. Pre-orders are non-refundable once the fulfillment window begins, but you may cancel
                        within 24 hours by emailing us.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">5. Acceptable use</h2>
                    <p>You agree not to:</p>
                    <ul className="list-disc list-inside space-y-1 mt-2">
                        <li>Submit content that infringes copyright or contains malware.</li>
                        <li>Attempt to reverse-engineer, scrape at scale, or DDoS the service.</li>
                        <li>Use Real-Check to harass, defame, or mislead others.</li>
                        <li>Use Real-Check to make life-threatening decisions without human verification.</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">6. Intellectual property</h2>
                    <p>
                        The Real-Check brand, compass logo, and UI are © 2025–2026 Real-Check.org. You retain ownership of any content you submit.
                        By submitting content, you grant us a limited license to process it through our AI providers to generate a verdict for you.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">7. Disclaimer &amp; limitation of liability</h2>
                    <p>
                        Real-Check is provided <strong className="text-accent">&quot;as is&quot;</strong>, without warranty of any kind. To the maximum extent
                        permitted by law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the
                        service. Our total liability is capped at the greater of $50 or the amount you paid us in the last 12 months.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">8. Changes to these Terms</h2>
                    <p>
                        We may update these Terms from time to time. Material changes will be announced on the homepage or by email. Continued use
                        after changes means you accept the new Terms.
                    </p>
                </section>

                <section>
                    <h2 className="text-accent font-display text-2xl mt-8 mb-2">9. Contact</h2>
                    <p>
                        Questions? Email <a href="mailto:hello@real-check.org" className="text-primary underline">hello@real-check.org</a>.
                        See our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link> for how we handle your data.
                    </p>
                </section>
            </div>
        </div>
    );
}
